/* Hallmark · genre: editorial · macrostructure: Split Studio · theme: design.md-locked warm paper · enrichment: original key visual + purpose-built collateral · nav: shared N9 · footer: Ft6 Letter close
 * structure: paired identity thesis/proof hero · asymmetric proof gallery · paired system and usage modules · contrast: pass (40–41) · honest: pass (46) · chrome: pass (47) · tokens: pass (48) · responsive: pass (34, 49, 50–57) · icons: pass (30)
 * Hallmark · pre-emit critique: P5 H5 E5 S5 R5 V5
 */
import React from "react";
import { useContent } from "@thoughtbot/superglue";
import { ArrowDownToLineIcon, CheckIcon } from "lucide-react";

import { BrandMark } from "@javascript/components/BrandMark";
import { MarketingFooter } from "@javascript/components/MarketingFooter";
import { Button } from "@javascript/components/ui/button";

const linkClass = "whitespace-nowrap font-medium text-primary underline decoration-primary/40 underline-offset-4 transition-colors hover:text-foreground focus-visible:rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring active:text-foreground";

type PreviewAsset = {
  label: string;
  description: string;
  imagePath: string;
  alt: string;
  width: number;
  height: number;
};

type SwatchToken = "paper" | "ink" | "gold" | "champagne" | "antique-gold" | "night";

type Swatch = {
  label: string;
  value: string;
  token: SwatchToken;
};

type BrandKitContent = {
  lockupSvgPath: string;
  keyVisualPath: string;
  previewAssets: PreviewAsset[];
  swatches: Swatch[];
  downloadUrl: string;
};

const previewLayoutClasses = [
  "lg:col-span-6",
  "lg:col-span-6",
  "sm:col-span-1 lg:col-span-4",
  "sm:col-span-1 lg:col-span-4",
  "sm:col-span-2 lg:col-span-4",
] as const;

const swatchClasses: Record<SwatchToken, string> = {
  paper: "bg-brand-kit-paper",
  ink: "bg-brand-kit-ink",
  gold: "bg-brand-kit-gold",
  champagne: "bg-brand-kit-champagne",
  "antique-gold": "bg-brand-kit-antique-gold",
  night: "bg-brand-kit-night",
};

export default function BrandKitShow() {
  const { lockupSvgPath, keyVisualPath, previewAssets, swatches, downloadUrl } = useContent<BrandKitContent>();

  return (
    <>
      <main>
        <section className="overflow-hidden bg-foreground text-background">
          <div className="mx-auto grid w-full max-w-6xl gap-12 px-4 pb-24 pt-16 sm:px-6 sm:pb-28 sm:pt-20 lg:grid-cols-[minmax(0,1.05fr)_minmax(0,0.95fr)] lg:items-center">
            <div className="min-w-0">
              <BrandMark size="md" className="font-serif font-semibold text-background" />
              <p className="mt-16 text-sm font-medium tracking-[0.18em] text-brand-kit-gold">Brand kit</p>
              <h1 className="mt-5 min-w-0 max-w-3xl text-balance font-serif text-5xl font-semibold leading-[1.02] tracking-tight [overflow-wrap:anywhere] sm:text-7xl">
                A system with more than one move.
              </h1>
              <p className="mt-7 max-w-xl text-lg leading-relaxed text-background/70">
                Logos, color, typography, pattern, an editorial key visual, and channel-ready collateral—each built for a different job while staying recognizably Goldypaper.
              </p>
              <div className="mt-9">
                <Button asChild size="lg" className="min-h-11">
                  <a href={downloadUrl} download>
                    <ArrowDownToLineIcon aria-hidden />
                    Download brand kit (.zip)
                  </a>
                </Button>
              </div>
            </div>

            <figure className="relative min-w-0 lg:max-w-[26rem] lg:translate-x-8 lg:justify-self-end">
              <div aria-hidden className="absolute -left-7 -top-7 size-32 rounded-full border border-primary/50" />
              <div aria-hidden className="absolute -left-1 top-2 size-32 rounded-full border border-primary/30" />
              <img
                src={keyVisualPath}
                alt="Layered Goldypaper paper key visual with restrained gold lines"
                width={1600}
                height={2000}
                fetchPriority="high"
                className="relative aspect-[4/5] w-full object-cover shadow-2xl"
              />
              <figcaption className="mt-4 flex items-center justify-between gap-4 text-sm text-background/60">
                <span>Editorial key visual</span>
                <span>Crop-ready · 4:5</span>
              </figcaption>
            </figure>
          </div>
        </section>

        <section className="px-4 py-16 sm:px-6 sm:py-24" aria-labelledby="inside-heading">
          <div className="mx-auto w-full max-w-6xl">
            <header className="grid gap-5 border-b pb-9 lg:grid-cols-[minmax(0,1fr)_minmax(18rem,0.45fr)] lg:items-end">
              <div>
                <h2 id="inside-heading" className="min-w-0 font-serif text-4xl font-semibold tracking-tight [overflow-wrap:anywhere] sm:text-5xl">What is inside</h2>
                <p className="mt-4 max-w-2xl text-lg leading-relaxed text-muted-foreground">
                  Core identity assets establish consistency. The social set demonstrates range through different messages, structures, and formats.
                </p>
              </div>
              <p className="text-sm leading-relaxed text-muted-foreground lg:text-right">
                28 files · SVG sources · ready-sized PNG exports · Markdown usage guide
              </p>
            </header>

            <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-12">
              {previewAssets.map((asset, index) => (
                <figure key={asset.imagePath} className={`min-w-0 ${previewLayoutClasses[index] ?? "lg:col-span-4"}`}>
                  <div className="overflow-hidden border bg-muted">
                    <img
                      data-brand-kit-preview
                      src={asset.imagePath}
                      alt={asset.alt}
                      width={asset.width}
                      height={asset.height}
                      loading="lazy"
                      className="h-auto w-full"
                    />
                  </div>
                  <figcaption className="mt-3 flex items-start justify-between gap-4">
                    <span className="font-medium">{asset.label}</span>
                    <span className="text-right text-sm text-muted-foreground">{asset.description}</span>
                  </figcaption>
                </figure>
              ))}
            </div>
          </div>
        </section>

        <section className="border-y bg-muted px-4 py-16 sm:px-6 sm:py-24" aria-labelledby="system-heading">
          <div className="mx-auto grid w-full max-w-6xl gap-12 lg:grid-cols-[minmax(0,0.85fr)_minmax(0,1.15fr)]">
            <div>
              <h2 id="system-heading" className="min-w-0 font-serif text-4xl font-semibold tracking-tight [overflow-wrap:anywhere]">The identity system</h2>
              <p className="mt-5 max-w-xl leading-relaxed text-muted-foreground">
                The original gradient mark remains the signature. Flat ink and reverse lockups handle practical reproduction; the supporting system keeps gold in its proper role—as punctuation, not wallpaper.
              </p>
              <div className="mt-9 space-y-5">
                <div className="border-b pb-5">
                  <p className="font-medium">Primary lockup</p>
                  <img src={lockupSvgPath} alt="The Goldypaper rings mark beside the wordmark" width={168} height={27} className="mt-4 h-10 w-auto max-w-full" />
                </div>
                <div className="grid gap-5 sm:grid-cols-2">
                  <div>
                    <p className="font-medium">Display</p>
                    <p className="mt-2 font-serif text-3xl font-semibold">Iowan Old Style</p>
                    <p className="mt-2 text-sm text-muted-foreground">Semibold · roman · editorial scale</p>
                  </div>
                  <div>
                    <p className="font-medium">Body</p>
                    <p className="mt-2 text-2xl">System sans</p>
                    <p className="mt-2 text-sm text-muted-foreground">Plain language · sentence case</p>
                  </div>
                </div>
              </div>
            </div>

            <ul className="grid grid-cols-2 gap-px overflow-hidden border bg-border sm:grid-cols-3">
              {swatches.map(({ label, value, token }) => (
                <li key={value} className="bg-background p-4">
                  <span data-brand-kit-swatch aria-hidden className={`block aspect-square w-full border ${swatchClasses[token]}`} />
                  <span className="mt-3 block text-sm font-medium">{label}</span>
                  <span className="mt-1 block font-mono text-xs text-muted-foreground">{value}</span>
                </li>
              ))}
            </ul>
          </div>
        </section>

        <section className="px-4 py-16 sm:px-6 sm:py-24" aria-labelledby="guidelines-heading">
          <div className="mx-auto grid w-full max-w-6xl gap-12 lg:grid-cols-[minmax(0,0.7fr)_minmax(0,1.3fr)]">
            <div>
              <h2 id="guidelines-heading" className="min-w-0 font-serif text-4xl font-semibold tracking-tight [overflow-wrap:anywhere]">Use it with care.</h2>
              <p className="mt-5 leading-relaxed text-muted-foreground">
                The README in the download maps every filename to its intended job, format, and copy.
              </p>
            </div>
            <ul className="divide-y border-y">
              {[
                "Leave clear space around the mark equal to at least half the rings icon's width.",
                "Use ink on paper or paper on night for reading; never set small body copy in gold.",
                "Do not stretch, rotate, outline, shadow, or rebuild the wordmark.",
                "Do not add invented metrics, testimonials, customer logos, or endorsements.",
              ].map((guideline) => (
                <li key={guideline} className="flex gap-4 py-5">
                  <CheckIcon aria-hidden className="mt-0.5 size-5 shrink-0 text-primary" />
                  <span>{guideline}</span>
                </li>
              ))}
            </ul>
          </div>
          <p className="mx-auto mt-12 w-full max-w-6xl text-sm text-muted-foreground">
            Need another format? <a className={linkClass} href="mailto:contact@goldypaper.com">contact@goldypaper.com</a>.
          </p>
        </section>
      </main>

      <MarketingFooter illustration={false} variant="letter-close" />
    </>
  );
}
